// Polyfill requestIdleCallback for iOS Safari < 18.4 / older WKWebView.
if (typeof window.requestIdleCallback === 'undefined') {
  window.requestIdleCallback = (cb) =>
    setTimeout(() => cb({ didTimeout: true, timeRemaining: () => 0 }), 1);
  window.cancelIdleCallback = (id) => clearTimeout(id);
}

import { useState, useEffect } from 'react';
import { createInertiaApp, router } from '@inertiajs/react';
// Registers its popstate listener on load, so before Inertia's (#524).
import '@/lib/history-interceptor';
import { http } from '@inertiajs/core';
import { createRoot } from 'react-dom/client';
import './application.css';
import * as Sentry from '@sentry/react';
import i18n, { loadLanguage, availableLanguages } from '@/lib/i18n';
import { resolveBootLanguage, whenLanguageReady } from '@/lib/boot-locale';
import { INERTIA_DEFAULTS } from '@/lib/inertia-defaults';
import {
  importWithChunkReload,
  installChunkReloadHandler,
  reloadForBoundaryError,
} from '@/lib/chunk-reload';

// Recover from stale-deploy chunk-load failures before any lazy import can fail.
installChunkReloadHandler();

import { initCapacitor, isAndroid, isNative } from '@/lib/capacitor';
import initAndroidRipple from '@/lib/android-ripple';
import initWebVitals from '@/lib/web-vitals';
import { initPushNotifications } from '@/lib/push-notifications';
import { configureRevenueCat, loginRevenueCat } from '@/lib/revenuecat';
import { initSentry, setSentryUser, SentryErrorBoundary } from '@/lib/sentry';
import { removeRetiredStorage } from '@/lib/retired-storage';
import { installSessionEndCleanup } from '@/lib/session-end';
import { freshenPage } from '@/lib/history-restore';
import {
  installPrefetchInvalidation,
  stampPageRender,
} from '@/lib/prefetch-invalidation';
import {
  initAnalytics,
  identifyUser,
  clearUser,
  trackEvent,
  trackPageView,
  bindAnalyticsFlush,
  syncAnalyticsPageContext,
} from '@/lib/analytics';
import { Toaster } from '@/components/ui/sonner';
import { toast } from '@/lib/haptic-toast';
import { ThemeProvider } from '@/lib/theme-provider';
import AccessibilityProvider from '@/lib/accessibility-provider';
import NavigationProgress from '@/components/navigation-progress';
// Imported statically (not lazy) on purpose: as a separate chunk this loaded as
// `consent-banner-<hash>.js`, whose name matches ad-blocker filter lists, so
// privacy extensions blocked it with net::ERR_BLOCKED_BY_CLIENT. The blocked
// dynamic import then crashed React.lazy with "reading 'default'" and white-
// screened the whole app (BOSKO-FRONTEND-4C). Bundling it into the entry means
// there is no separate request to block. It renders null when no notice is due,
// so the static cost is trivial.
import CookieBanner from '@/components/consent-banner';
import FormGuardProvider from '@/lib/form-guard-context';
import { isTabSwitch } from '@/lib/tab-navigation';
import {
  installCsrfMetaSync,
  installInertiaCsrfFallback,
} from '@/lib/inertia-csrf';
import { installInertiaNetworkErrorHandler } from '@/lib/inertia-network-error';
import { installInertiaHttpExceptionHandler } from '@/lib/inertia-http-exception';
import usePendingPurchaseLanding from '@/hooks/use-pending-purchase-landing';

function InertiaShell({ App, props }) {
  const [currentUser, setCurrentUser] = useState(
    () => props.initialPage.props.currentUser,
  );
  // Here, not in AppLayout: the shell is the one component every page shares
  // (AppLayout remounts between layouts; the tour renders outside it), so a
  // deferred store purchase is watched once for the app's lifetime.
  usePendingPurchaseLanding(props.initialPage);

  useEffect(() => {
    return router.on('navigate', (event) => {
      const user = event.detail.page.props.currentUser;
      if (user) setCurrentUser(user);
    });
  }, []);

  return (
    <AccessibilityProvider currentUser={currentUser}>
      <FormGuardProvider>
        <NavigationProgress />
        <App {...props} />
        <Toaster />
        <CookieBanner />
      </FormGuardProvider>
    </AccessibilityProvider>
  );
}

const pages = import.meta.glob('../pages/**/*.jsx');
const resolvedPages = {};

function toKebabPath(name) {
  return name
    .split('/')
    .map((segment) => segment.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase())
    .join('/');
}

async function resolvePageComponent(name, page) {
  // Before the page renders: a page restored from history, or a prefetched
  // one, gets the signed-in user's current state (#524).
  freshenPage(page);
  stampPageRender(page);
  const kebab = toKebabPath(name);
  const path = `../pages/${kebab}.jsx`;
  const importFn = pages[path];
  if (!importFn) {
    throw new Error(
      `Page not found: ${path}. Available pages: ${Object.keys(pages).join(', ')}`,
    );
  }

  const mod = await importWithChunkReload(importFn);
  resolvedPages[path] = mod;
  return mod;
}

createInertiaApp({
  defaults: INERTIA_DEFAULTS,
  resolve: resolvePageComponent,
  setup({ el, App, props }) {
    initSentry({
      dsn: props.initialPage.props.sentryDsn,
      environment: props.initialPage.props.sentryEnvironment,
      release: props.initialPage.props.sentryRelease,
    });

    removeRetiredStorage();

    const currentUser = props.initialPage.props.currentUser;
    setSentryUser(currentUser);
    if (currentUser) identifyUser(currentUser);

    const mountApp = () => {
      const root = el._reactRoot || createRoot(el);
      el._reactRoot = root;
      root.render(
        <SentryErrorBoundary
          fallback={<p>Something went wrong.</p>}
          onError={reloadForBoundaryError}
        >
          <ThemeProvider>
            <InertiaShell App={App} props={props} />
          </ThemeProvider>
        </SentryErrorBoundary>,
      );

      // Firebase is optional to the first paint. Import it during idle time;
      // analytics.js queues page views, events, and identity changes until the
      // SDK is ready, so this removes the universal Firebase cost without
      // losing cold-start funnel data.
      if (!el._analyticsInitScheduled) {
        el._analyticsInitScheduled = true;
        window.requestIdleCallback(
          () => {
            initAnalytics().catch((e) =>
              Sentry.addBreadcrumb({
                category: 'analytics',
                message: 'Failed to initialize',
                level: 'warning',
                data: { error: String(e) },
              }),
            );
          },
          { timeout: 2000 },
        );
      }

      // Reveal the app only after it has painted — in the user's language. The
      // #app-loading spinner is language-neutral, so keeping it up until now
      // covers the async bundle load: no English first frame, and no blank gap
      // between hiding the spinner and the first localized paint.
      const loader = document.getElementById('app-loading');
      if (loader) {
        loader.style.opacity = '0';
        setTimeout(() => loader.remove(), 200);
      }
    };

    // Hold the first render until the user's language bundle is active, so the
    // app never flashes English before switching to the selected language.
    // Every supported language, including English, waits for its bundle. The
    // wait is capped so a stalled request cannot hang the app; a failed
    // non-English bundle loads English as the recovery catalog.
    const userLang = resolveBootLanguage(
      currentUser,
      props.initialPage.props.locale,
      availableLanguages,
    );
    whenLanguageReady(userLang, {
      i18n,
      loadLanguage,
      onError: (e) =>
        Sentry.addBreadcrumb({
          category: 'i18n',
          message: 'Failed to load language',
          level: 'warning',
          data: { error: String(e) },
        }),
    }).then(mountApp);
    // Register Inertia router listeners exactly once. createInertiaApp may
    // invoke setup() more than once (StrictMode double-invoke, Vite HMR);
    // without this guard, every navigation would fire each handler twice
    // and analytics events would be double-counted. Pattern matches the
    // existing el._reactRoot cache above.
    if (!el._inertiaListenersBound) {
      el._inertiaListenersBound = true;
      installInertiaCsrfFallback(http, {
        addBreadcrumb: Sentry.addBreadcrumb,
        captureMessage: Sentry.captureMessage,
      });
      installInertiaNetworkErrorHandler(router, {
        notifyOffline: () => toast.error(i18n.t('common.offline')),
        addBreadcrumb: Sentry.addBreadcrumb,
      });
      installSessionEndCleanup(router, props.initialPage);
      installPrefetchInvalidation(router);
      installCsrfMetaSync(router);
      installInertiaHttpExceptionHandler(router, {
        notify: (key) => toast.error(i18n.t(key)),
        redirect: (url) => {
          window.location.href = url;
        },
        addBreadcrumb: Sentry.addBreadcrumb,
      });

      // Track GA4 page_view (web) and screen_view (native parity) on every
      // Inertia navigation — including the initial visit, which Inertia
      // fires `navigate` for too. Firebase auto-page_view is disabled in
      // firebase.js, so this is the single source of truth for SPA page
      // tracking.
      //
      // Inertia + React StrictMode dispatches the initial `navigate` event
      // twice (mount + remount under StrictMode), so we dedupe consecutive
      // events for the same URL+component to avoid double-counting. A real
      // re-visit of the same URL after navigating elsewhere still fires
      // normally because lastNavKey gets overwritten by the intermediate
      // page.
      let lastNavKey = null;
      router.on('navigate', (event) => {
        // Re-pin the sanitized page context for the new URL. Inertia fires
        // `navigate` after the new page has mounted, so its mount effects'
        // events came first; analytics.js keeps a private page private until
        // this sync. Outside the lastNavKey dedupe on purpose — the pin must
        // track every URL change, deduped or not.
        syncAnalyticsPageContext();
        const navKey = `${event.detail.page.url}|${event.detail.page.component}`;
        if (navKey === lastNavKey) return;
        lastNavKey = navKey;
        // screen_view is the native (Firebase mobile) equivalent of page_view.
        // On web we already fire page_view below, so emitting screen_view too
        // just doubled per-navigation event volume — keep it native-only.
        if (isNative()) {
          trackEvent('screen_view', {
            screen_name: event.detail.page.component,
          });
        }
        // Snapshot the URL now; defer the page_view ~100ms so the new page
        // has rendered and <Head>'s useEffect has applied document.title
        // before we read it. (Measured: title updates ~50ms after navigate
        // in this environment; 100ms gives headroom.) rAF is unreliable in
        // headless/background tabs.
        const pageLocation = window.location.href;
        setTimeout(() => {
          // <Head> has applied the new page's title by now: re-pin the
          // page_title default, which the navigate-time pin above took from
          // the previous page.
          syncAnalyticsPageContext();
          trackPageView({
            page_location: pageLocation,
            page_title: document.title,
          });
        }, 100);
      });

      // Server-confirmed events (sign_up, login, email_verified, …) arrive as
      // the analyticsEvents prop after a PRG redirect — flush them on every
      // navigation, including the initial visit. Runs outside the lastNavKey
      // dedupe above on purpose: the flush has its own per-event-id dedupe,
      // and tying it to the screen_view dedupe would drop events delivered on
      // a StrictMode-duplicated initial navigate.
      // Binds 'navigate' AND 'success' — see bindAnalyticsFlush. Binding only
      // 'navigate' silently dropped every event bridged over a same-URL
      // redirect, which is why message_limit_reached and memory_verse_added
      // never reached GA4 while every cross-URL bridged event did.
      // Replace visits (bottom-nav and top-bar tabs, same-URL redirects) fire
      // `success` but never `navigate`, so re-pin the page context here too,
      // and again once <Head> has applied the new title. Registered before
      // the flush so events flushed on `success` see a profile's title as
      // private (#521).
      router.on('success', () => {
        syncAnalyticsPageContext();
        setTimeout(syncAnalyticsPageContext, 100);
      });
      bindAnalyticsFlush(router);

      // Re-identify on navigation so an in-session login/logout/upgrade updates
      // GA4's user_id + user properties. identifyUser runs once at boot (above),
      // so before this a mid-session login fired events with no user_id and a
      // mid-session upgrade kept user_plan='Free' until a hard reload — breaking
      // cross-device stitching and plan-based segmentation for the exact
      // (just-converted) users the paywall analysis cares about most. Keyed on
      // id+plan+language so a plan/language change re-fires, not just a login.
      // No-ops in dev (analytics isn't initialized). Mirrors the RevenueCat
      // re-login handler below.
      const identityKeyOf = (u) =>
        u?.id ? `${u.id}|${u.plan || ''}|${u.language || ''}` : null;
      let lastIdentityKey = identityKeyOf(currentUser);
      router.on('navigate', (event) => {
        const user = event.detail.page.props.currentUser;
        const key = identityKeyOf(user);
        if (key === lastIdentityKey) return;
        lastIdentityKey = key;
        if (user?.id) {
          identifyUser(user);
        } else {
          clearUser();
        }
      });

      // Activate the user's language on navigation whenever it changes — most
      // importantly the onboarding regional step, which saves a new language and
      // redirects into the tour. Boot only sets the language once (via
      // whenLanguageReady below), and the tour + onboarding screens render
      // OUTSIDE AppLayout, so AppLayout's useLocaleSync never runs there — without
      // this the app stays in the previous language until a full reload (the
      // onboarding→tour language handoff defect, #361). Keyed on the language
      // alone; changeLanguage is idempotent, so this composes with useLocaleSync
      // on the pages that do have it.
      let lastLang =
        currentUser?.language || props.initialPage.props.locale || 'en';
      router.on('navigate', (event) => {
        const lang = event.detail.page.props.currentUser?.language;
        if (!lang || lang === lastLang) return;
        lastLang = lang;
        if (!availableLanguages.has(lang)) return;
        loadLanguage(lang)
          .then(() => i18n.changeLanguage(lang))
          .then(() => {
            document.documentElement.lang = lang;
            document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
          })
          .catch((e) =>
            Sentry.addBreadcrumb({
              category: 'i18n',
              message: 'Failed to activate language on navigation',
              level: 'warning',
              data: { error: String(e) },
            }),
          );
      });

      // Add subtle page transition on Inertia navigations (skip when persistent layout stays mounted)
      let prevLayout = null;
      router.on('navigate', (event) => {
        // Tab switches are handled by PageTransition — skip layout-level animation too
        if (isTabSwitch()) return;

        const kebab = toKebabPath(event.detail.page.component);
        const page = resolvedPages[`../pages/${kebab}.jsx`];
        const layout = page?.default?.layout;
        const layoutChanged = layout !== prevLayout;
        prevLayout = layout;
        if (!layoutChanged) return;
        const main = document.getElementById('main-content');
        if (main) {
          main.classList.remove('page-transition-enter');
          // Use requestAnimationFrame instead of forced reflow to restart animation
          requestAnimationFrame(() => {
            main.classList.add('page-transition-enter');
          });
        }
      });
    }

    initCapacitor(props.initialPage).catch((e) =>
      Sentry.addBreadcrumb({
        category: 'capacitor',
        message: 'Failed to initialize',
        level: 'warning',
        data: { error: String(e) },
      }),
    );
    if (isAndroid()) initAndroidRipple();
    initWebVitals();
    const routes = props.initialPage.props.routes;
    if (currentUser && routes) {
      initPushNotifications(routes).catch((e) =>
        Sentry.addBreadcrumb({
          category: 'push',
          message: 'Failed to initialize',
          level: 'warning',
          data: { error: String(e) },
        }),
      );
    }

    // RevenueCat: configure on boot for the current user (mobile only).
    // Non-native platforms no-op; missing API keys log a warning and no-op.
    if (isNative() && currentUser?.id) {
      configureRevenueCat(currentUser.id).catch((e) =>
        Sentry.addBreadcrumb({
          category: 'revenuecat',
          message: 'Configure failed',
          level: 'warning',
          data: { error: String(e) },
        }),
      );
    }

    // Switch RevenueCat user on Inertia navigations after login/logout.
    let lastRcUserId = currentUser?.id || null;
    router.on('navigate', (event) => {
      if (!isNative()) return;
      const navUserId = event.detail.page.props.currentUser?.id || null;
      if (navUserId === lastRcUserId) return;
      lastRcUserId = navUserId;
      if (navUserId) {
        loginRevenueCat(navUserId).catch(() => {});
      }
    });
  },
});
